import { LinkProps } from '@tanstack/react-router';
import { FilterFieldOperator } from '@/components/table/_filterFieldOperators/FilterFieldOperator.ts';
import { AttentionItemDto } from '@/types/stats/AttentionItemDto.ts';
import { AttentionKey } from '@/features/dashboard/attention/types/AttentionKey.ts';
import { FinanceView } from '@/features/dashboard/tours/routes/tourFinanceView.ts';
import { FinancesTab } from '@/features/dashboard/finances/routes/financesTabs.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import { AggregatedPaymentStatus } from '@/features/dashboard/financialTransactions/types/AggregatedPaymentStatus.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { ConsentStatus } from '@/features/dashboard/consentTemplates/types/ConsentStatus.ts';
import { SimpleNestedBookingChange } from '@/types/SimpleNestedBookingChange.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import { MessageStatus } from '@/features/dashboard/messages/types/MessageEnums.ts';

/** Cel przejścia jako dane: literał trasy sprawdza kompilator, więc przemianowany segment wywala build, nie klik. */
export type AttentionLink = Pick<LinkProps, 'to' | 'params' | 'search'>;

/**
 * Zasięg panelu, z którego wychodzi wiersz — wyznacza trasę, nigdy filtr (§18.2); wyjątki to reszty transakcji
 * (§18.7) i niepodpisane dokumenty (§18.9.4). Panel spraw całej firmy dopisze `{ kind: 'company' }` i własną mapę.
 */
export type AttentionLinkScope = { kind: 'tour'; tourId: string };

const overdueLines = [
  {
    f: 'paymentStatus',
    o: FilterFieldOperator.IS,
    v: PlannedTransactionPaymentStatus.OVERDUE,
  },
];

// Anulowanie gasi każdą niedopłaconą linię, więc wpłata częściowa zostaje za linią CANCELLED — stąd wszystkie
// linie anulowanych rezerwacji, nie tylko zapłacone.
const cancelledBookingLines = [
  { f: 'bookingStatus', o: FilterFieldOperator.IS, v: BookingStatus.CANCELLED },
];

const overdueTourPayments = (tourId: string): AttentionLink => ({
  to: '/app/tours/$tourId/finances',
  params: { tourId },
  search: { view: FinanceView.PAYMENTS, filters: overdueLines },
});

const tourBudget = (tourId: string): AttentionLink => ({
  to: '/app/tours/$tourId/finances',
  params: { tourId },
  search: { view: FinanceView.BUDGET },
});

const tourSettings = (tourId: string): AttentionLink => ({
  to: '/app/tours/$tourId/config/basics',
  params: { tourId },
});

const tourOptions = (tourId: string): AttentionLink => ({
  to: '/app/tours/$tourId/options',
  params: { tourId },
});

const tourParticipants = (tourId: string): AttentionLink => ({
  to: '/app/tours/$tourId/participants',
  params: { tourId },
});

const tourClients = (tourId: string): AttentionLink => ({
  to: '/app/tours/$tourId/clients',
  params: { tourId },
});

/** Paczka, gdy wiersz mówi o dokładnie jednej — tylko wtedy jest jeden ekran do otwarcia. */
const singleChange = (
  item: AttentionItemDto,
): SimpleNestedBookingChange | null => {
  const ref = item.refs[0];
  return item.count === 1 && ref?.kind === 'CHANGE' ? ref.change : null;
};

const changeScreen = (
  change: SimpleNestedBookingChange,
  step: ChangePackageStep,
): AttentionLink => ({
  to: '/app/bookings/$bookingId/changes/$changeId',
  params: { bookingId: change.bookingId, changeId: change.id },
  search: { step },
});

// Wyjazd nie ma listy paczek, a firmowa lista rezerwacji wyprowadziłaby z ekranów wyjazdu (§18.10).
const changeOrTourClients =
  (open: (change: SimpleNestedBookingChange) => AttentionLink) =>
  (tourId: string, item: AttentionItemDto): AttentionLink => {
    const change = singleChange(item);
    return change ? open(change) : tourClients(tourId);
  };

/** Pola i wartości sprawdzone w hydratorach backendu — gdzie żadne pole nie wyraża stanu, link idzie bez filtra. */
const TOUR_LINKS: Record<
  AttentionKey,
  (tourId: string, item: AttentionItemDto) => AttentionLink
> = {
  [AttentionKey.OVERDUE_INSTALLMENTS]: overdueTourPayments,
  // Linie zwrotu siedzą w tej samej tabeli planowanych wpłat, która nie ma pola rodzaju linii.
  [AttentionKey.OVERDUE_REFUNDS]: overdueTourPayments,
  // Pole `booking` przyjmuje jedną rezerwację, a filtry adresu łączą się tylko przez AND — stąd status, nie lista kodów.
  [AttentionKey.CANCELLED_BOOKING_HOLDING_MONEY]: (tourId) => ({
    to: '/app/tours/$tourId/finances',
    params: { tourId },
    search: { view: FinanceView.PAYMENTS, filters: cancelledBookingLines },
  }),
  [AttentionKey.OVERPAYMENT]: (tourId) => ({
    to: '/app/tours/$tourId/clients',
    params: { tourId },
    search: {
      filters: [
        {
          f: 'paymentStatus',
          o: FilterFieldOperator.IS,
          v: AggregatedPaymentStatus.OVERPAYMENT,
        },
      ],
    },
  }),
  // Wyjazd nie ma tabeli transakcji, więc listę firmy zawęża filtr `tour` — te same nogi co sprawa.
  [AttentionKey.UNALLOCATED_TRANSACTIONS]: (tourId) => ({
    to: '/app/finances',
    search: {
      tab: FinancesTab.TRANSACTIONS,
      quick: { withUnallocatedRemainder: true },
      filters: [{ f: 'tour', o: FilterFieldOperator.EQUALS, v: tourId }],
    },
  }),
  [AttentionKey.BUDGET_OVERRUN]: tourBudget,
  [AttentionKey.OVERDUE_EXPENSES]: (tourId) => ({
    to: '/app/tours/$tourId/finances',
    params: { tourId },
    search: { view: FinanceView.BUDGET, filters: overdueLines },
  }),
  [AttentionKey.MISSING_BANK_ACCOUNT_FOR_CURRENCY]: tourSettings,
  [AttentionKey.TOUR_LIMIT_BELOW_OCCUPANCY]: tourSettings,
  [AttentionKey.MANDATORY_TRANSPORT_SOLD_OUT]: tourSettings,
  [AttentionKey.OPTION_LIMIT_BELOW_OCCUPANCY]: tourOptions,
  [AttentionKey.REQUIRED_OPTION_SOLD_OUT]: tourOptions,
  // Wyjazd nie ma listy umów, więc listę firmy zawęża pole `tourId`; bez `hideUnsigned: false` niepodpisanych nie widać wcale.
  [AttentionKey.UNSIGNED_REQUIRED_CONSENT]: (tourId) => ({
    to: '/app/contracts',
    search: {
      quick: { hideUnsigned: false },
      filters: [
        { f: 'tourId', o: FilterFieldOperator.EQUALS, v: tourId },
        {
          f: 'status',
          o: FilterFieldOperator.IS,
          v: ConsentStatus.NOT_ACCEPTED,
        },
      ],
    },
  }),
  [AttentionKey.PARTICIPANT_WITHOUT_REQUIRED_INSURANCE]: tourParticipants,
  [AttentionKey.MISSING_REQUIRED_FIELD]: tourParticipants,
  [AttentionKey.WAITLIST_WITH_FREE_SEATS]: (tourId) => ({
    to: '/app/tours/$tourId/waitlist',
    params: { tourId },
  }),
  [AttentionKey.PENDING_CLIENT_CHANGE]: changeOrTourClients((change) =>
    changeScreen(change, ChangePackageStep.ACCEPTANCE),
  ),
  [AttentionKey.STALE_DRAFT_CHANGE]: changeOrTourClients((change) =>
    changeScreen(change, ChangePackageStep.SUMMARY),
  ),
  // Ekran przepływu pokazuje tylko paczkę otwartą — odrzuconą widać w historii zmian rezerwacji.
  [AttentionKey.CHANGE_REJECTED_BY_CLIENT]: changeOrTourClients((change) => ({
    to: '/app/bookings/$bookingId/history',
    params: { bookingId: change.bookingId },
  })),
  [AttentionKey.MESSAGE_FAILED]: (tourId) => ({
    to: '/app/tours/$tourId/messages',
    params: { tourId },
    search: {
      filters: [
        { f: 'sendStatus', o: FilterFieldOperator.IS, v: MessageStatus.FAILED },
      ],
    },
  }),
  [AttentionKey.RECIPIENT_EMAIL_BOUNCING]: tourClients,
};

/** Jedyne miejsce, które zamienia sprawę na adres. */
export const attentionItemLink = (
  item: AttentionItemDto,
  scope: AttentionLinkScope,
): AttentionLink => {
  const link = TOUR_LINKS[item.key as AttentionKey];
  // Nieznany klucz zgłasza już rejestr zdań; wiersz i tak musi dokądś prowadzić.
  return link
    ? link(scope.tourId, item)
    : { to: '/app/tours/$tourId/summary', params: { tourId: scope.tourId } };
};
